// packages/support-types/src/index.ts
/**
 * Shared TypeScript contracts for the AYILand customer-support stack.
 *
 * Imported by:
 *  - apps/support-service        (backend: REST + WebSocket gateway)
 *  - apps/support-console        (front: agent multi-conversation app)
 *  - packages/support-widget     (front: customer chat bubble)
 *
 * Pure TS, browser- and Node-safe — no runtime dependencies.
 */

// ─────────────────────────────────────────────────────────────────────────────
//  Enums — kept as string literal unions so both Prisma enum values *and*
//  plain JSON wire payloads stay assignable without runtime coupling.
// ─────────────────────────────────────────────────────────────────────────────

export type SupportChannel =
  | 'WIDGET_WEB'
  | 'WIDGET_MOBILE'
  | 'WHATSAPP'
  | 'EMAIL'
  | 'DISPUTE';

export type SupportTicketStatus =
  | 'OPEN'
  | 'AI_ASSIST'
  | 'QUEUED'
  | 'ASSIGNED'
  | 'IN_PROGRESS'
  | 'WAITING_USER'
  | 'RESOLVED'
  | 'CLOSED';

export type SupportPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';

export type SupportCategory =
  | 'GENERAL'
  | 'ACCOUNT'
  | 'KYC'
  | 'PAYMENT'
  | 'PARCEL'
  | 'LOAN'
  | 'DISPUTE'
  | 'TECHNICAL'
  | 'FRAUD';

export type SupportSenderType = 'USER' | 'AGENT' | 'AI' | 'SYSTEM';

export type SupportAgentStatus = 'ONLINE' | 'BUSY' | 'AWAY' | 'OFFLINE';

export type SupportLanguage = 'fr' | 'en' | 'fon' | 'yo' | 'ee' | 'ha' | 'es';

// ─────────────────────────────────────────────────────────────────────────────
//  Wire-level DTOs (JSON-safe — no Date, only ISO strings)
// ─────────────────────────────────────────────────────────────────────────────

export interface SupportAttachment {
  /** Storage URL (signed, short-lived in prod). */
  url: string;
  /** Original file name as uploaded by the user. */
  name: string;
  mime: string;
  /** Size in bytes. */
  size: number;
  /** AV scan timestamp (ISO). Absent until scanned. */
  scannedAt?: string;
}

export interface SupportMessageDTO {
  id: string;
  ticketId: string;
  senderType: SupportSenderType;
  senderId: string | null;
  body: string;
  attachments?: SupportAttachment[];
  /** Internal notes are NEVER sent to the user — filtered server-side. */
  internalNote: boolean;
  readByUser: boolean;
  readByAgent: boolean;
  /** ISO timestamp. */
  createdAt: string;
  /** Optional client-supplied id for optimistic UI dedup. */
  clientId?: string;
}

export interface SupportAgentPublicDTO {
  id: string;
  displayName: string;
  status: SupportAgentStatus;
  /** Optional avatar URL surfaced in the widget header. */
  avatar?: string | null;
}

export interface SupportTicketSummaryDTO {
  id: string;
  number: string;
  subject: string;
  status: SupportTicketStatus;
  priority: SupportPriority;
  category: SupportCategory;
  channel: SupportChannel;
  language: SupportLanguage;
  tags: string[];
  assignedAgent: SupportAgentPublicDTO | null;
  unreadCountForUser: number;
  unreadCountForAgent: number;
  lastMessagePreview: string | null;
  lastMessageAt: string | null;
  /** ISO timestamps. */
  createdAt: string;
  slaResponseDeadline: string | null;
}

export interface SupportTicketDetailDTO extends SupportTicketSummaryDTO {
  messages: SupportMessageDTO[];
}

/** Sidebar context shown to agents (read-only snapshot of main API data). */
export interface SupportUserContextDTO {
  userId: string;
  displayName: string;
  email: string;
  phoneNumber: string | null;
  phoneVerified: boolean;
  kycLevel: string;
  kycStatus: string | null;
  walletBSC: string | null;
  riskLevel: string;
  riskScore: number;
  /** Counts of related resources, deep links pulled lazily. */
  parcelsCount: number;
  activeLoansCount: number;
  totalLoansCount: number;
  lastActivityAt: string | null;
  /** Recent payments / parcels / loans (5 most recent each). */
  recentPayments: Array<{
    id: string;
    amount: string;
    currency: string;
    status: string;
    createdAt: string;
  }>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  REST request / response shapes
// ─────────────────────────────────────────────────────────────────────────────

export interface CreateTicketInput {
  subject: string;
  category?: SupportCategory;
  priority?: SupportPriority;
  language?: SupportLanguage;
  /** Initial message body. Required for a new conversation. */
  initialMessage: string;
  channel?: Extract<SupportChannel, 'WIDGET_WEB' | 'WIDGET_MOBILE'>;
}

export interface PostMessageInput {
  body: string;
  attachments?: SupportAttachment[];
  /** Agent-only: post as internal note (filtered for the customer). */
  internalNote?: boolean;
  /** Client id for optimistic UI dedup. */
  clientId?: string;
}

export interface CsatInput {
  score: 1 | 2 | 3 | 4 | 5;
  comment?: string;
}

export interface AgentUpdateTicketInput {
  status?: SupportTicketStatus;
  priority?: SupportPriority;
  tags?: string[];
  category?: SupportCategory;
}

// ─────────────────────────────────────────────────────────────────────────────
//  WebSocket event contracts
//
//  Path: /ws/support  (token passed in the first `auth` frame, never in URL).
//  Discriminator: `type`.
// ─────────────────────────────────────────────────────────────────────────────

export type ClientToServer =
  | { type: 'auth'; token: string }
  | { type: 'join'; ticketId: string }
  | { type: 'leave'; ticketId: string }
  | { type: 'message'; ticketId: string; body: string; attachments?: SupportAttachment[]; internalNote?: boolean; clientId?: string }
  | { type: 'typing'; ticketId: string; isTyping: boolean }
  | { type: 'read'; ticketId: string; messageId: string }
  | { type: 'ping' };

export type ServerToClient =
  | { type: 'hello'; serverTime: string; protocolVersion: 1 }
  | { type: 'auth_ok'; userId: string; role: 'USER' | 'AGENT'; agentId?: string }
  | { type: 'auth_error'; message: string }
  | { type: 'message'; message: SupportMessageDTO }
  | { type: 'typing'; ticketId: string; senderType: SupportSenderType; senderId: string | null; isTyping: boolean }
  | { type: 'status_change'; ticketId: string; status: SupportTicketStatus; at: string }
  | { type: 'agent_joined'; ticketId: string; agent: SupportAgentPublicDTO }
  | { type: 'agent_left'; ticketId: string; agentId: string }
  | { type: 'presence'; agentId: string; status: SupportAgentStatus }
  | { type: 'read'; ticketId: string; messageId: string; by: SupportSenderType }
  | { type: 'error'; code: string; message: string }
  | { type: 'pong' };

// ─────────────────────────────────────────────────────────────────────────────
//  Hard limits — shared by client + server validation
// ─────────────────────────────────────────────────────────────────────────────

export const SUPPORT_LIMITS = {
  /** Max characters in a single message body. */
  MESSAGE_MAX_LEN: 4000,
  /** Max subject length on ticket create. */
  SUBJECT_MAX_LEN: 200,
  /** Max attachments per message. */
  ATTACHMENTS_PER_MESSAGE: 5,
  /** Max attachment size in bytes (10 MiB). */
  ATTACHMENT_MAX_BYTES: 10 * 1024 * 1024,
  /** Allowed attachment MIME types. */
  ATTACHMENT_MIME_WHITELIST: [
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/gif',
    'application/pdf',
  ] as const,
  /** WebSocket path. */
  WS_PATH: '/ws/support',
  /** Heartbeat ping interval (ms). */
  WS_PING_INTERVAL_MS: 25_000,
  /** Default SLA response deadline by priority (minutes). */
  SLA_RESPONSE_MINUTES: { LOW: 240, NORMAL: 60, HIGH: 30, URGENT: 10 } as Record<
    SupportPriority,
    number
  >,
} as const;

export type AttachmentMime =
  (typeof SUPPORT_LIMITS.ATTACHMENT_MIME_WHITELIST)[number];
